import { Bill, BillSortValue } from "@common/types/bill";
import { BillColumn } from "@common/types/views/billViews";
import { ViewConfig } from "@lib/views";
import {
    AMOUNT_COLUMN_SIZE,
    DATE_COLUMN_SIZE,
    SCHEDULE_COLUMN_SIZE,
} from "@design-system/ViewTable/DefaultColumnSizes";
import { DateCell } from "./cells/DateCell";
import { LockCell } from "./cells/LockCell";
import { StatusCell } from "./cells/StatusCell";
import { VendorCell } from "./cells/VendorCell";
import { AccountCell } from "./cells/AccountCell";
import { AmountCell } from "./cells/AmountCell";
import { OpenAmountCell } from "./cells/OpenAmountCell";
import { EntityCell } from "./cells/EntityCell";
import { ExternalLinkCell } from "./cells/ExternalLinkCell";
import { BillNumberCell } from "./cells/BillNumberCell";
import { ScheduleCell } from "./cells/ScheduleCell";
import { MatchCell } from "./cells/MatchCell";

interface CreateBillsViewConfigParams {
    hasAnyAccrualLedgers: boolean;
    isSchedulesEnabled?: boolean;
}

export const createBillsViewConfig = ({
    hasAnyAccrualLedgers,
    isSchedulesEnabled = false,
}: CreateBillsViewConfigParams): ViewConfig<
    Bill,
    BillColumn,
    BillSortValue
> => ({
    columns: {
        [BillColumn.LOCK]: {
            accessorKey: "id",
            name: "Lock",
            header: "",
            cell: LockCell,
            immutable: true,
            hiddenFromSettings: true,
            minSize: 32,
            maxSize: 32,
        },
        [BillColumn.DATE]: {
            accessorKey: "dateOnly",
            name: "Created date",
            header: "Created date",
            cell: DateCell,
            sort: {
                asc: BillSortValue.DATE_ASC,
                desc: BillSortValue.DATE_DESC,
            },
            ...DATE_COLUMN_SIZE,
        },
        [BillColumn.STATUS]: {
            accessorKey: "status",
            name: "Status",
            header: "",
            cell: StatusCell,
            sort: {
                asc: BillSortValue.STATUS_ASC,
                desc: BillSortValue.STATUS_DESC,
            },
            minSize: 24,
            maxSize: 24,
            internal: !hasAnyAccrualLedgers,
        },
        [BillColumn.EXTERNAL_STATUS]: {
            accessorKey: "externalStatus",
            name: "External status",
            header: "",
            cell: StatusCell,
            sort: {
                asc: BillSortValue.EXTERNAL_STATUS_ASC,
                desc: BillSortValue.EXTERNAL_STATUS_DESC,
            },
            minSize: 24,
            maxSize: 24,
        },
        [BillColumn.INVOICE_NUMBER]: {
            accessorKey: "invoiceNumber",
            name: "Bill number",
            header: "Bill number",
            cell: BillNumberCell,
            minSize: 120,
            maxSize: 120,
        },
        [BillColumn.VENDOR]: {
            accessorKey: "counterparty",
            name: "Vendor",
            header: "Vendor",
            cell: VendorCell,
            sort: {
                asc: BillSortValue.COUNTERPARTY_ASC,
                desc: BillSortValue.COUNTERPARTY_DESC,
            },
            minSize: 200,
            maxSize: 300,
        },
        [BillColumn.ACCOUNT]: {
            accessorKey: "financialAccount",
            name: "Account",
            header: "Account",
            cell: AccountCell,
            sort: {
                asc: BillSortValue.ACCOUNT_ASC,
                desc: BillSortValue.ACCOUNT_DESC,
            },
            minSize: 120,
            maxSize: 180,
        },
        [BillColumn.DUE_DATE]: {
            accessorKey: "dueDateOnly",
            name: "Due date",
            header: "Due date",
            cell: DateCell,
            sort: {
                asc: BillSortValue.DUE_DATE_ASC,
                desc: BillSortValue.DUE_DATE_DESC,
            },
            ...DATE_COLUMN_SIZE,
        },
        [BillColumn.AMOUNT]: {
            accessorKey: "total",
            name: "Amount",
            header: "Amount",
            cell: AmountCell,
            sort: {
                asc: BillSortValue.AMOUNT_ASC,
                desc: BillSortValue.AMOUNT_DESC,
            },
            ...AMOUNT_COLUMN_SIZE,
        },
        [BillColumn.OPEN]: {
            accessorKey: "total",
            name: "Open",
            header: "Open",
            cell: OpenAmountCell,
            ...AMOUNT_COLUMN_SIZE,
        },
        [BillColumn.SCHEDULE]: {
            accessorKey: "schedule.scheduleName",
            name: "Expense schedule",
            header: "Expense schedule",
            cell: ScheduleCell,
            ...SCHEDULE_COLUMN_SIZE,
            internal: !isSchedulesEnabled,
        },
        [BillColumn.MATCH]: {
            accessorKey: "id",
            name: "Match",
            header: "Match",
            cell: MatchCell,
            minSize: 140,
            maxSize: 220,
            internal: !hasAnyAccrualLedgers,
        },
        [BillColumn.ENTITY]: {
            accessorKey: "financialAccount.entity",
            name: "Entity",
            header: "",
            cell: EntityCell,
            minSize: 42,
            maxSize: 42,
        },
        [BillColumn.EXTERNAL_LINK]: {
            accessorKey: "id",
            name: "External Link",
            header: "",
            cell: ExternalLinkCell,
            immutable: true,
            hiddenFromSettings: true,
            minSize: 48,
            maxSize: 48,
        },
    },
});
